import type { ReactNode } from 'react'
import type { NextPageWithLayout } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Bell, Building2, Palette, UserRound } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { PageHeader } from '@/components/dashboard/page-header'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProfileForm } from '@/components/settings/profile-form'
import { WorkspaceForm } from '@/components/settings/workspace-form'
import { NotificationsForm } from '@/components/settings/notifications-form'
import { AppearanceForm } from '@/components/settings/appearance-form'

const TABS = [
    { value: 'profil', label: 'Profil', icon: UserRound },
    { value: 'workspace', label: 'Workspace', icon: Building2 },
    { value: 'notifikasi', label: 'Notifikasi', icon: Bell },
    { value: 'tampilan', label: 'Tampilan', icon: Palette }
] as const

type TabValue = typeof TABS[number]['value']

const isTab = (value: unknown): value is TabValue => typeof value === 'string' && TABS.some((tab) => tab.value === value)

const SettingsPage: NextPageWithLayout = (): ReactNode => {
    const router = useRouter()
    const [tab, setTab] = useState<TabValue>('profil')

    // Keep the open tab in the address so links like ?tab=workspace work
    useEffect(() => {
        if (!router.isReady) return

        const requested = router.query.tab
        if (isTab(requested)) setTab(requested)
    }, [router.isReady, router.query.tab])

    const onTabChange = (value: unknown): void => {
        if (!isTab(value)) return

        setTab(value)
        router.replace(value === 'profil' ? '/pengaturan' : `/pengaturan?tab=${value}`, undefined, { shallow: true, scroll: false })
    }

    return (
        <>
            <Seo title='Pengaturan' description='Profil, workspace, notifikasi, dan tampilan dasbor Arunika.' />

            <div className='space-y-6'>
                <PageHeader
                    title='Pengaturan'
                    description='Atur profil Anda, identitas workspace, kabar yang ingin diterima, dan tampilan dasbor.'
                />

                <Tabs value={tab} onValueChange={onTabChange} className='gap-6'>
                    <div className='no-scrollbar -mx-1 overflow-x-auto border-b px-1'>
                        <TabsList variant='line' className='h-auto gap-1 p-0 group-data-horizontal/tabs:h-auto'>
                            {TABS.map((item) => {
                                const Icon = item.icon

                                return (
                                    <TabsTrigger key={item.value} value={item.value} className='h-10 flex-none px-3 group-data-horizontal/tabs:after:bottom-0'>
                                        <Icon aria-hidden='true' />
                                        {item.label}
                                    </TabsTrigger>
                                )
                            })}
                        </TabsList>
                    </div>

                    <div className='max-w-3xl'>
                        <TabsContent value='profil' keepMounted>
                            <ProfileForm />
                        </TabsContent>
                        <TabsContent value='workspace' keepMounted>
                            <WorkspaceForm />
                        </TabsContent>
                        <TabsContent value='notifikasi' keepMounted>
                            <NotificationsForm />
                        </TabsContent>
                        <TabsContent value='tampilan' keepMounted>
                            <AppearanceForm />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </>
    )
}

export default SettingsPage
